import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import { 
    Zap, Plus, Search, RefreshCw, User, 
    Calendar, ExternalLink, Paperclip, Camera, 
    Send, X, CheckCircle2, AlertCircle, 
    ArrowUpCircle, MinusCircle, Clock, ChevronDown, 
    MessageSquare, FileText
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const CorrectiveActionManager = ({ onNavigate }) => {
    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [currentUser, setCurrentUser] = useState(null); 
    
    // UI State
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('OPEN'); 
    const [onlyMyActions, setOnlyMyActions] = useState(false); 
    const [expandedId, setExpandedId] = useState(null);

    // Note & File State
    const [newNote, setNewNote] = useState('');
    const [noteFile, setNoteFile] = useState(null);
    const fileInputRef = useRef(null);

    // Dropdown Data
    const [users, setUsers] = useState([]);
    const [audits, setAudits] = useState([]);
    const [incidents, setIncidents] = useState([]);

    // Form Data
    const [formData, setFormData] = useState({
        title: '', description: '', priority: 'MEDIUM', due_date: '',
        assigned_to: '', source_type: 'GENERAL', linked_audit: '', linked_incident: ''
    });

    useEffect(() => { 
        loadData(); 
        api.get('users/me/').then(res => setCurrentUser(res.data)).catch(console.error);
    }, []);

    const getData = (res) => Array.isArray(res.data) ? res.data : (res.data.results || []);

    const loadData = async () => {
        setLoading(true);
        try {
            const actRes = await api.get('actions/');
            setActions(getData(actRes));

            const results = await Promise.allSettled([
                api.get('users/manage/'), 
                api.get('audits/'),       
                api.get('safety/')        
            ]);

            if (results[0].status === 'fulfilled') setUsers(getData(results[0].value));
            if (results[1].status === 'fulfilled') setAudits(getData(results[1].value));
            if (results[2].status === 'fulfilled') setIncidents(getData(results[2].value));

        } catch (err) { 
            console.error("Failed to load data", err); 
        } finally { 
            setLoading(false); 
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (!payload.due_date) payload.due_date = null;

            if (payload.source_type === 'GENERAL') { payload.linked_audit = null; payload.linked_incident = null; }
            if (payload.source_type === 'AUDIT') { payload.linked_incident = null; }
            if (payload.source_type === 'INCIDENT') { payload.linked_audit = null; }

            await api.post('actions/', payload);
            alert("Action Created Successfully");
            setShowForm(false);
            setFormData({ title: '', description: '', priority: 'MEDIUM', due_date: '', assigned_to: '', source_type: 'GENERAL', linked_audit: '', linked_incident: '' });
            loadData();
        } catch (err) { alert("Error creating action: " + JSON.stringify(err.response?.data || "Unknown Error")); }
    };

    const handleAssigneeUpdate = async (actionId, newAssigneeId) => {
        if (!window.confirm("Are you sure you want to reassign this action? This will be logged.")) return;
        try {
            await api.patch(`actions/${actionId}/`, { assigned_to: newAssigneeId });
            loadData(); 
        } catch (err) { alert("Failed to reassign user."); }
    };

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            await api.patch(`actions/${id}/`, { status: newStatus });
            loadData(); 
        } catch (err) { alert("Update failed"); }
    };

    const handleAddNote = async (e, actionId) => {
        e.preventDefault();
        if(!newNote.trim() && !noteFile) return;
        
        try {
            const formData = new FormData();
            formData.append('text', newNote);
            if (noteFile) {
                formData.append('image', noteFile);
            }

            await api.post(`actions/${actionId}/note/`, formData);
            
            setNewNote('');
            setNoteFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            
            loadData(); 
        } catch (err) { alert("Failed to add update"); }
    };

    // --- UPDATED PERMISSION CHECK ---
    const canCreate = useMemo(() => {
        if (!currentUser) return false;
        
        if (currentUser.is_superuser) return true;
        if (currentUser.is_staff) return true; 
        if (currentUser.is_site_owner) return true;

        const allowedRoles = ['SAFE_ENG', 'SAFETY_MGR', 'PROJ_MANAGER_CUST', 'PROJ_MANAGER_CONT'];
        if (allowedRoles.includes(currentUser.role)) return true;

        return false;
    }, [currentUser]);

    // Filter Logic
    const filteredActions = useMemo(() => {
        if (!Array.isArray(actions)) return [];
        return actions.filter(a => {
            const searchLower = searchQuery.toLowerCase();
            const matchSearch = (a.title || '').toLowerCase().includes(searchLower) ||
                                (a.description || '').toLowerCase().includes(searchLower) ||
                                (a.assigned_to_name || '').toLowerCase().includes(searchLower) ||
                                (a.display_id || '').toLowerCase().includes(searchLower) || 
                                a.id.toString().includes(searchLower);

            let matchStatus = true;
            if (statusFilter === 'OPEN') matchStatus = a.status !== 'COMPLETED' && a.status !== 'VERIFIED';
            if (statusFilter === 'CLOSED') matchStatus = a.status === 'COMPLETED' || a.status === 'VERIFIED';
            
            const matchMyActions = !onlyMyActions || (currentUser && a.assigned_to == currentUser.id);

            return matchSearch && matchStatus && matchMyActions;
        });
    }, [actions, searchQuery, statusFilter, onlyMyActions, currentUser]);

    // --- HELPERS ---
    const getPriorityIcon = (p) => {
        if (p === 'CRITICAL') return <AlertCircle size={14} color={THEME.danger} />;
        if (p === 'HIGH') return <ArrowUpCircle size={14} color={THEME.warning} />;
        return <MinusCircle size={14} color={THEME.success} />;
    };

    const getPriorityColor = (p) => {
        if (p === 'CRITICAL' || p === 'HIGH') return THEME.danger;
        if (p === 'MEDIUM') return THEME.warning;
        return THEME.success; 
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem', boxSizing:'border-box' };

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <Zap size={28} color={THEME.primary} />
                    <div>
                        <h2 style={{color: 'white', margin:0}}>Corrective Actions</h2>
                        <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>Track and assign fixes for issues.</p>
                    </div>
                </div>
                
                {/* BUTTON IS NOW VISIBLE FOR STAFF */}
                {canCreate && (
                    <button onClick={() => setShowForm(!showForm)} style={{padding:'10px 20px', background: THEME.primary, color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                        {showForm ? <X size={18}/> : <Plus size={18}/>}
                        {showForm ? 'Cancel' : 'New Action'}
                    </button>
                )}
            </div>

            {/* CREATE FORM */}
            {showForm && canCreate && (
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'30px', borderTop:`4px solid ${THEME.primary}`}}>
                    <h3 style={{color:'white', marginTop:0, marginBottom:'20px'}}>Create Corrective Action</h3>
                    <form onSubmit={handleSubmit} style={{display:'grid', gap:'15px', gridTemplateColumns: '1fr 1fr'}}>
                        
                        <div style={{gridColumn: 'span 2'}}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Issue Title</label>
                            <input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} style={inputStyle} required placeholder="e.g. Fix broken guardrail" />
                        </div>
                        <div style={{gridColumn: 'span 2'}}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Description</label>
                            <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} style={{...inputStyle, height:'80px'}} placeholder="Details..." />
                        </div>
                        
                        <div>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Source Type</label>
                            <select value={formData.source_type} onChange={e => setFormData({...formData, source_type: e.target.value})} style={inputStyle}>
                                <option value="GENERAL">General Task</option>
                                <option value="AUDIT">From Audit</option>
                                <option value="INCIDENT">From Incident</option>
                            </select>
                        </div>
                        
                        <div style={{zIndex: 50}}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Reference ID (Searchable)</label>
                            {formData.source_type === 'AUDIT' ? (
                                <SearchableDropdown 
                                    options={audits} value={formData.linked_audit} onChange={(id) => setFormData({...formData, linked_audit: id})}
                                    placeholder="-- Search Audit --" labelFn={(a) => `${a.display_id || 'Audit #'+a.id} - ${a.location}`} 
                                />
                            ) : formData.source_type === 'INCIDENT' ? (
                                <SearchableDropdown 
                                    options={incidents} value={formData.linked_incident} onChange={(id) => setFormData({...formData, linked_incident: id})}
                                    placeholder="-- Search Incident --" labelFn={(i) => `${i.display_id || 'Report #'+i.id} - ${i.report_type}`} 
                                />
                            ) : (
                                <input disabled placeholder="N/A - General Task" style={{...inputStyle, opacity:0.5}} />
                            )}
                        </div>
                        
                        <div style={{zIndex: 49}}> 
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Assign To (Searchable)</label>
                            <SearchableDropdown 
                                options={users} value={formData.assigned_to} onChange={(id) => setFormData({...formData, assigned_to: id})}
                                placeholder="-- Search User --" labelFn={(u) => `${u.username} (${u.company_name || 'Staff'})`}
                            />
                        </div>

                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
                            <div>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Priority</label>
                                <select value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})} style={inputStyle}>
                                    <option value="MEDIUM">Medium</option>
                                    <option value="HIGH">High</option>
                                    <option value="CRITICAL">Critical</option>
                                </select>
                            </div>
                            <div>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Due Date</label>
                                <input type="date" value={formData.due_date} onChange={e => setFormData({...formData, due_date: e.target.value})} style={inputStyle} />
                            </div>
                        </div>

                        <div style={{gridColumn: 'span 2', textAlign:'right'}}>
                            <button type="submit" style={{padding:'10px 25px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Create Action</button>
                        </div>
                    </form>
                </div>
            )}

            {/* FILTERS */}
            <div style={{marginBottom:'20px', background:THEME.card, padding:'15px', borderRadius:'12px', border:`1px solid ${THEME.border}`, display:'flex', gap:'15px', flexWrap:'wrap', alignItems:'center'}}>
                
                <button 
                    onClick={() => setOnlyMyActions(!onlyMyActions)}
                    style={{
                        padding:'8px 16px', 
                        borderRadius:'6px', 
                        border: `1px solid ${onlyMyActions ? THEME.primary : THEME.border}`, 
                        background: onlyMyActions ? 'rgba(59, 130, 246, 0.2)' : 'transparent', 
                        color: onlyMyActions ? 'white' : THEME.textSec,
                        cursor:'pointer',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <User size={16}/> My Actions
                </button>

                <div style={{width:'1px', height:'20px', background:THEME.border}}></div>

                <div style={{position:'relative', flex: 2, minWidth:'200px'}}>
                    <Search size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                    <input 
                        placeholder="Search actions (ID, Title, Assignee)..." 
                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)} 
                        style={{...inputStyle, background:'#0b1120', marginBottom:0, paddingLeft:'35px'}} 
                    />
                </div>
                <select 
                    value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    style={{...inputStyle, flex: 1, minWidth:'150px', background:'#0b1120', marginBottom:0}}
                >
                    <option value="OPEN">Open Actions</option>
                    <option value="CLOSED">Closed / Verified</option>
                    <option value="ALL">All Actions</option>
                </select>
                <button onClick={() => {setSearchQuery(''); setStatusFilter('OPEN'); setOnlyMyActions(false);}} style={{padding:'8px 12px', background:'transparent', border:`1px solid ${THEME.textSec}`, color:THEME.textSec, borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}>
                    <RefreshCw size={14}/> Reset
                </button>
            </div>

            {/* TABLE LIST */}
            {loading ? <div style={{color:'white', padding:'20px', textAlign:'center'}}>Loading...</div> : (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', textAlign:'left', fontSize:'0.9rem'}}>
                        <thead style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px', color:THEME.textSec, width:'100px'}}>ID</th>
                                <th style={{padding:'15px', color:THEME.textSec}}>Title</th>
                                <th style={{padding:'15px', color:THEME.textSec}}>Assignee</th>
                                <th style={{padding:'15px', color:THEME.textSec}}>Priority</th>
                                <th style={{padding:'15px', color:THEME.textSec}}>Due Date</th>
                                <th style={{padding:'15px', color:THEME.textSec}}>Status</th>
                                <th style={{padding:'15px', textAlign:'right', color:THEME.textSec}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredActions.length === 0 ? (
                                <tr><td colSpan="7" style={{padding:'30px', textAlign:'center', color:THEME.textSec}}>No actions found matching your filters.</td></tr>
                            ) : (
                                filteredActions.map(act => (
                                    <React.Fragment key={act.id}>
                                        <tr onClick={() => setExpandedId(expandedId === act.id ? null : act.id)} style={{borderBottom: `1px solid ${THEME.border}`, cursor: 'pointer', background: expandedId === act.id ? 'rgba(59, 130, 246, 0.05)' : 'transparent', transition: 'background 0.2s'}}>
                                            <td style={{padding:'15px', color:THEME.primary, fontWeight:'bold'}}>{act.display_id || '#'+act.id}</td>
                                            
                                            <td style={{padding:'15px', color:'white', fontWeight:500}}>
                                                {act.title}
                                                {currentUser && act.assigned_to == currentUser.id && <span style={{marginLeft:'8px', fontSize:'0.7rem', background: 'rgba(168, 85, 247, 0.2)', color:'#a855f7', padding:'2px 6px', borderRadius:'4px', border:'1px solid #a855f7'}}>ME</span>}
                                            </td>
                                            <td style={{padding:'15px', color:THEME.textSec}}>{act.assigned_to_name || 'Unassigned'}</td>
                                            <td style={{padding:'15px'}}>
                                                <span style={{color: getPriorityColor(act.priority), fontWeight:'bold', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:'5px'}}>
                                                    {getPriorityIcon(act.priority)} {act.priority}
                                                </span>
                                            </td>
                                            <td style={{padding:'15px', color:THEME.textSec}}>{act.due_date || '-'}</td>
                                            <td style={{padding:'15px'}}>
                                                <select 
                                                    onClick={(e) => e.stopPropagation()} 
                                                    value={act.status} 
                                                    onChange={(e) => handleStatusUpdate(act.id, e.target.value)} 
                                                    style={{padding:'4px 8px', borderRadius:'6px', background:'#0f172a', border:`1px solid ${THEME.border}`, color:'white', fontSize:'0.85rem'}}
                                                >
                                                    <option value="OPEN">Open</option>
                                                    <option value="IN_PROGRESS">In Progress</option>
                                                    <option value="COMPLETED">Completed</option>
                                                    {(canCreate || act.status === 'VERIFIED') && (
                                                        <option value="VERIFIED">Verified / Closed</option>
                                                    )}
                                                </select>
                                            </td>
                                            <td style={{padding:'15px', textAlign:'right', color:THEME.primary, fontWeight:'bold'}}>{expandedId === act.id ? 'Close' : 'View'}</td>
                                        </tr>

                                        {expandedId === act.id && (
                                            <tr style={{background:'#020617', borderBottom:`2px solid ${THEME.border}`}}>
                                                <td colSpan="7" style={{padding:'0'}}>
                                                    <div style={{display:'flex', flexDirection:'column', gap:'0'}}>
                                                        
                                                        {/* Top Section */}
                                                        <div style={{padding:'20px', borderBottom:`1px dashed ${THEME.border}`, display:'grid', gridTemplateColumns:'2fr 1fr', gap:'20px'}}>
                                                            <div>
                                                                <h4 style={{marginTop:0, color:'white', fontSize:'1rem'}}>Description</h4>
                                                                <div style={{color:THEME.textSec, fontSize:'0.95rem', marginBottom:'15px', lineHeight:'1.5'}}>{act.description || "No description provided."}</div>

                                                                {/* --- CREATED BY DETAILS --- */}
                                                                <div style={{marginBottom:'15px', paddingBottom:'15px', borderBottom:`1px dashed ${THEME.border}`, fontSize:'0.85rem', color:THEME.textSec, display:'flex', alignItems:'center', gap:'15px'}}>
                                                                    <span style={{display:'flex', alignItems:'center', gap:'5px'}}><User size={14}/> Created by: <span style={{color:'white'}}>{act.created_by_name}</span> {act.company_name ? `(${act.company_name})` : ''}</span>
                                                                    <span style={{display:'flex', alignItems:'center', gap:'5px', opacity:0.7}}><Calendar size={14}/> {new Date(act.created_at).toLocaleDateString()}</span>
                                                                </div>
                                                                
                                                                <div style={{display:'flex', gap:'10px'}}>
                                                                        {act.linked_audit && <button onClick={() => onNavigate('audits', act.linked_audit)} style={{background: 'rgba(250, 204, 21, 0.1)', border: `1px solid ${THEME.warning}`, color: THEME.warning, padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize:'0.8rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'6px'}}><ExternalLink size={14}/> Jump to Audit {act.linked_audit_label ? '' : `#${act.linked_audit}`}</button>}
                                                                        {act.linked_incident && <button onClick={() => onNavigate('safety', act.linked_incident)} style={{background: 'rgba(239, 68, 68, 0.1)', border: `1px solid ${THEME.danger}`, color: THEME.danger, padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize:'0.8rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'6px'}}><ExternalLink size={14}/> Jump to Incident {act.linked_incident_label ? '' : `#${act.linked_incident}`}</button>}
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Re-Assign Dropdown */}
                                                            <div style={{background:'#0f172a', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px', textTransform:'uppercase'}}>Next Action / Assignee</label>
                                                                <SearchableDropdown 
                                                                    options={users} 
                                                                    value={act.assigned_to} 
                                                                    onChange={(newId) => handleAssigneeUpdate(act.id, newId)}
                                                                    placeholder="-- Select User --" 
                                                                    labelFn={(u) => `${u.username} (${u.company_name || 'Staff'})`}
                                                                />
                                                                <div style={{marginTop:'5px', fontSize:'0.75rem', color:THEME.textSec, fontStyle:'italic'}}>
                                                                    Changing this will be recorded in history.
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Bottom Section: Activity Log */}
                                                        <div style={{padding:'20px', background:'#0b1120'}}>
                                                            <h4 style={{color:THEME.primary, marginTop:0, marginBottom:'15px', fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.5px', display:'flex', alignItems:'center', gap:'8px'}}><MessageSquare size={16}/> Activity Log & Notes</h4>
                                                            <div style={{display:'flex', flexDirection:'column', gap:'12px', maxHeight:'300px', overflowY:'auto', marginBottom:'15px'}}>
                                                                {act.logs && act.logs.length > 0 ? act.logs.map(log => (
                                                                    <div key={log.id} style={{paddingLeft:'10px', borderLeft:`2px solid ${THEME.border}`}}>
                                                                        <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.75rem', color:THEME.textSec, marginBottom:'4px'}}>
                                                                            <span><strong>{log.author_name}</strong></span>
                                                                            <span style={{display:'flex', alignItems:'center', gap:'4px'}}><Clock size={12}/> {new Date(log.created_at).toLocaleString()}</span>
                                                                        </div>
                                                                        <div style={{color:'#cbd5e1', fontSize:'0.9rem'}}>{log.text}</div>
                                                                        
                                                                        {log.image && (
                                                                            <div style={{marginTop:'8px'}}>
                                                                                <a href={log.image} target="_blank" rel="noopener noreferrer" style={{display:'flex', alignItems:'center', gap:'5px', textDecoration:'none', color:THEME.primary, fontSize:'0.85rem'}}>
                                                                                    <FileText size={14}/> View Attachment
                                                                                </a>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )) : <div style={{color:THEME.textSec, fontStyle:'italic', fontSize:'0.9rem'}}>No updates yet.</div>}
                                                            </div>

                                                            <form onSubmit={(e) => handleAddNote(e, act.id)} style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                                                {noteFile && (
                                                                    <div style={{display:'flex', alignItems:'center', gap:'10px', background:`${THEME.primary}22`, padding:'8px', borderRadius:'6px', width:'fit-content'}}>
                                                                        <span style={{color:THEME.primary, fontSize:'0.85rem', display:'flex', alignItems:'center', gap:'5px'}}><Paperclip size={14}/> {noteFile.name}</span>
                                                                        <button type="button" onClick={() => {setNoteFile(null); if(fileInputRef.current) fileInputRef.current.value="";}} style={{background:'transparent', border:'none', color:THEME.danger, fontWeight:'bold', cursor:'pointer'}}><X size={14}/></button>
                                                                    </div>
                                                                )}

                                                                <div style={{display:'flex', gap:'10px'}}>
                                                                    <input 
                                                                        type="file" 
                                                                        accept="image/*"
                                                                        ref={fileInputRef}
                                                                        style={{display:'none'}}
                                                                        onChange={(e) => setNoteFile(e.target.files[0])}
                                                                    />
                                                                    
                                                                    <button type="button" onClick={() => fileInputRef.current.click()} style={{padding:'0 15px', background:'#1e293b', border:`1px solid ${THEME.border}`, borderRadius:'6px', cursor:'pointer', color:THEME.textSec}} title="Attach Image">
                                                                        <Camera size={18}/>
                                                                    </button>

                                                                    <input 
                                                                        value={newNote} 
                                                                        onChange={e => setNewNote(e.target.value)} 
                                                                        placeholder="Type a note or update..." 
                                                                        style={{flex:1, padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:THEME.card, color:'white'}} 
                                                                    />
                                                                    <button type="submit" style={{padding:'0 20px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}>
                                                                        <Send size={16}/> Send
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const SearchableDropdown = ({ options, value, onChange, placeholder, labelFn }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selected = options.find(o => o.id === parseInt(value));
    const filtered = options.filter(o => labelFn(o).toLowerCase().includes(search.toLowerCase()));

    return (
        <div style={{position:'relative'}}>
            <div onClick={() => setIsOpen(true)} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color: selected ? 'white' : '#94a3b8', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', boxSizing: 'border-box'}}>
                <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{selected ? labelFn(selected) : placeholder}</span>
                <ChevronDown size={16}/>
            </div>
            {isOpen && (
                <>
                    <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, zIndex:10}} onClick={() => setIsOpen(false)} />
                    <div style={{position:'absolute', top:'100%', left:0, right:0, zIndex:11, background:'#1e293b', border:`1px solid ${THEME.border}`, borderRadius:'6px', marginTop:'5px', maxHeight:'250px', overflowY:'auto', boxShadow:'0 10px 15px -3px rgba(0, 0, 0, 0.5)'}}>
                        <div style={{position:'sticky', top:0, background:'#1e293b', padding:'8px', borderBottom:`1px solid ${THEME.border}`}}>
                            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Type to filter..." style={{width:'100%', padding:'8px', borderRadius:'4px', background:'#0f172a', border:`1px solid ${THEME.border}`, color:'white', outline:'none', boxSizing:'border-box'}} />
                        </div>
                        {filtered.map(opt => (
                            <div key={opt.id} onClick={() => { onChange(opt.id); setIsOpen(false); setSearch(''); }} style={{padding:'10px', cursor:'pointer', borderBottom:'1px solid #334155', background: opt.id === parseInt(value) ? 'rgba(59, 130, 246, 0.2)' : 'transparent', color: opt.id === parseInt(value) ? THEME.primary : 'white', fontSize:'0.9rem'}} onMouseEnter={e => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = opt.id === parseInt(value) ? 'rgba(59, 130, 246, 0.2)' : 'transparent'}>
                                {labelFn(opt)}
                            </div>
                        ))}
                        {filtered.length === 0 && <div style={{padding:'15px', textAlign:'center', color:'#94a3b8', fontSize:'0.9rem'}}>No matches found.</div>}
                    </div>
                </>
            )}
        </div>
    );
};

export default CorrectiveActionManager;